import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import { SP_BREAKPOINT } from "../../constants/app";
import { PRIMARY_DARK_GREEN, PRIMARY_RED } from "../../constants/color";
import { NotificationItem, appActions } from "../../ducks/app";
import { AppDispatch } from "../../store";
import { CloseIcon } from "./icons/CloseIcon";
import { FailedIcon } from "./icons/notification/FailedIcon";
import { SuccessIcon } from "./icons/notification/SuccessIcon";

type Props = {
  notification: NotificationItem;
  isVisible: boolean;
};

export const Notification: React.FC<Props> = ({ notification, isVisible }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isShown, setIsShown] = useState<boolean>(false);
  const [notificationStatus, setNotificationStatus] = useState<
    NotificationItem["type"]
  >(notification.type);
  const [notificationMessage, setNotificationMessage] = useState<string>(
    notification.message,
  );

  const handleClose = useCallback(() => {
    dispatch(appActions.hideNotification());
  }, []);

  useEffect(() => {
    if (isVisible) {
      setIsShown(isVisible);
      setNotificationStatus(notification.type);
      setNotificationMessage(notification.message);
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setTimeout(() => {
        setIsShown(isVisible);
        setNotificationStatus(notification.type);
        setNotificationMessage(notification.message);
      }, 500);
    }
  }, [isVisible, handleClose, notification]);

  if (!isShown) return null;

  return (
    <StyledNotificationBackground $isVisible={isVisible}>
      <StyledNotificationContainer $type={notificationStatus}>
        <StyledNotificationContent>
          <StyledNotification>
            {notificationStatus === "success" && <SuccessIcon />}
            {notificationStatus === "failed" && <FailedIcon />}

            <StyledNotificationTitleContainer>
              <StyledNotificationTitle>
                {notificationMessage}
              </StyledNotificationTitle>
            </StyledNotificationTitleContainer>
          </StyledNotification>

          <StyledNotificationCloseButton onClick={handleClose}>
            <CloseIcon />
          </StyledNotificationCloseButton>
        </StyledNotificationContent>
      </StyledNotificationContainer>
    </StyledNotificationBackground>
  );
};

const easeInOut = ($isVisible: boolean) => keyframes`
  0% {
    right: ${$isVisible ? "-380px" : "30px"};
  }
  100% {
    right: ${$isVisible ? "30px" : "-380px"};
  }
`;

const easeInOutSP = ($isVisible: boolean) => keyframes`
  0% {
    right: ${$isVisible ? "-380px" : "0px"};
  }
  100% {
    right: ${$isVisible ? "0px" : "-380px"};
  }
`;

const StyledNotificationBackground = styled.div<{ $isVisible: boolean }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    right: ${({ $isVisible }) => ($isVisible ? "0px" : "-380px")};
    animation: ${({ $isVisible }) => easeInOutSP($isVisible)} 0.5s ease-in-out;
  }

  position: fixed;
  top: 30px;
  right: ${({ $isVisible }) => ($isVisible ? "30px" : "-380px")};
  animation: ${({ $isVisible }) => easeInOut($isVisible)} 0.5s ease-in-out;
  z-index: 9999;
`;

const StyledNotificationContainer = styled.div<{
  $type: NotificationItem["type"];
}>`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
    max-width: 100vw;
  }

  display: flex;
  width: 384px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  background-color: #ffffff;
  border: 2px solid
    ${({ $type }) => ($type === "success" ? PRIMARY_DARK_GREEN : PRIMARY_RED)};
  border-radius: 8px;
  box-shadow: 4px 6px 10px 0px rgba(0, 0, 0, 0.1);
`;

const StyledNotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  align-self: stretch;
`;

const StyledNotification = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1 0 0;
`;

const StyledNotificationTitleContainer = styled.div`
  display: flex;
  padding: 4px 0px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
`;

const StyledNotificationTitle = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
  }

  width: 275px;
  height: auto;
  flex: 1 0 0;
  align-self: stretch;
  color: #333333;
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledNotificationCloseButton = styled.div`
  cursor: pointer;
`;
